import { makeAutoObservable } from "mobx";
import type { SoleTraderQuestionnaireClient } from "./soleTraderQuestionnaireClient";
import {
  type SoleTraderQuestionnaireAnswer,
  type SoleTraderQuestionnaireConfig,
  type SoleTraderQuestionnaireQuestion,
} from "./soleTraderQuestionnaireTypes";

export class SoleTraderQuestionnaireStore {
  private _config: SoleTraderQuestionnaireConfig | null = null;
  private _isInitialized = false;

  constructor(private readonly _client: SoleTraderQuestionnaireClient) {
    makeAutoObservable(this);
  }

  get config(): SoleTraderQuestionnaireConfig | null {
    return this._config;
  }

  private set config(value: SoleTraderQuestionnaireConfig | null) {
    this._config = value;
  }

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  get firstQuestionId(): string {
    return this._config?.firstQuestionId ?? "";
  }

  async init(): Promise<void> {
    if (this._isInitialized) {
      return;
    }
    this._config = await this._client.getFlowConfig();
    this._isInitialized = true;
  }

  getQuestion(questionId: string): SoleTraderQuestionnaireQuestion | undefined {
    return this._config?.questions[questionId];
  }

  getAnswer(questionId: string, answerId: string): SoleTraderQuestionnaireAnswer | undefined {
    const question = this.getQuestion(questionId);
    return question?.answers[answerId];
  }

  getNextQuestionId(questionId: string, answerId: string): string {
    const answer = this.getAnswer(questionId, answerId);
    return answer?.nextQuestionId ?? "0";
  }

  isFlowComplete(nextQuestionId: string): boolean {
    return nextQuestionId === "0";
  }

  getOutcome(questionId: string, answerId: string): { outcomeId: string; outcomeText: string | null } | undefined {
    const answer = this.getAnswer(questionId, answerId);
    if (!answer) {
      return undefined;
    }
    return {
      outcomeId: answer.outcomeId,
      outcomeText: answer.outcomeText,
    };
  }
}
